import notes from '../data/notes.json';
import { dispatch, getState, type View } from '../state/store';

export interface Route {
  view: View;
  appId?: string;
  payload?: Record<string, string>;
}

const APP_IDS = new Set([
  'about',
  'projects',
  'games',
  'music',
  'notepad',
  'browser',
  'wrack',
  'control',
  'display',
  'sounds',
  'videos',
]);

const DEFAULT_NOTE =
  Array.isArray(notes) && notes[0] && typeof notes[0].id === 'string' ? notes[0].id : 'todo';

export function parseHash(hash: string): Route {
  let raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.startsWith('/')) raw = raw.slice(1);
  const parts = raw.split('/').filter(Boolean);
  if (parts.length === 0 || parts[0] === 'desktop' || parts[0] === 'room') {
    if (parts[0] === 'desktop' || parts[0] === 'room') {
      const rest = parts.slice(1);
      if (rest.length === 0) return { view: 'desktop' };
      return parseApp(rest, 'desktop');
    }
    return { view: 'desktop' };
  }
  return parseApp(parts, 'desktop');
}

function parseApp(parts: string[], view: View): Route {
  const app = parts[0];
  if (!app || !APP_IDS.has(app)) return { view };
  const rest = parts.slice(1);
  const payload: Record<string, string> = {};
  if (app === 'notepad') payload.noteId = rest[0] || DEFAULT_NOTE;
  if (app === 'browser') payload.pageId = rest[0] || 'ben';
  if (app === 'games' && rest[0]) payload.gameId = rest[0];
  if (app === 'videos' && rest[0]) payload.videoId = rest[0];
  return { view, appId: app, payload };
}

export function toHashFromRoute(route: Route): string {
  const segs: string[] = [];
  if (route.appId) {
    segs.push(route.appId);
    if (route.appId === 'notepad') segs.push(route.payload?.noteId || DEFAULT_NOTE);
    if (route.appId === 'browser' && route.payload?.pageId) segs.push(route.payload.pageId);
    if (route.appId === 'games' && route.payload?.gameId) segs.push(route.payload.gameId);
    if (route.appId === 'videos' && route.payload?.videoId) segs.push(route.payload.videoId);
  }
  return segs.length ? `#/${segs.join('/')}` : '#/';
}

export function toHash(state: {
  view: View;
  transition?: string;
  windows: { appId: string; state: string; z: number; payload?: Record<string, unknown> }[];
}): string {
  const top = [...state.windows]
    .filter((w) => w.state !== 'minimized')
    .sort((a, b) => b.z - a.z)[0];
  const payload: Record<string, string> = {};
  if (top?.payload) {
    for (const [k, v] of Object.entries(top.payload)) {
      if (typeof v === 'string') payload[k] = v;
    }
  }
  return toHashFromRoute({
    view: 'desktop',
    appId: top?.appId,
    payload: Object.keys(payload).length ? payload : undefined,
  });
}

export function normalize(hash: string): string {
  return toHashFromRoute(parseHash(hash));
}

export function applyRoute(route: Route): void {
  if (route.appId) {
    dispatch({ type: 'OPEN_WINDOW', appId: route.appId, payload: route.payload });
  }
}

let writing = false;

export function writeHash(mode: 'push' | 'replace'): void {
  const next = toHash(getState());
  if (normalize(location.hash || '#/') === next) return;
  writing = true;
  if (mode === 'replace') history.replaceState(null, '', next);
  else history.pushState(null, '', next);
  queueMicrotask(() => {
    writing = false;
  });
}

export function initRouter(): void {
  const boot = parseHash(location.hash || '#/');
  if (normalize(location.hash || '#/') !== (location.hash || '#/')) {
    history.replaceState(null, '', normalize(location.hash || '#/'));
  }
  applyRoute(boot);

  window.addEventListener('hashchange', () => {
    if (writing) return;
    const s = getState();
    const incoming = location.hash || '#/';
    if (normalize(incoming) === toHash(s)) return;
    applyRoute(parseHash(incoming));
  });
}

export function hrefForAction(href: string | undefined): string | undefined {
  return href;
}
